import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '@hooks/useDispatch';
import { setEditingCell } from '@store/Spreadsheet/editingSlice';
import { updateRow } from '@store/Spreadsheet/rowsSlice';
import { CommonCellProps } from '../Cell';
import classes from '../Cell.module.css';
import { cellValueChanged } from '@store/Spreadsheet/cellChangesSlice';

export default function CellNumber({ rowId, columnId, isEditing, value, currentRow, initialValue }: CommonCellProps ) {
  const dispatch = useAppDispatch();
  // TV1-2358 — see the long note in `CellString`: same two-effect split, same
  // reason. The type-to-edit seed cannot arrive through the useState initialiser
  // because this component is never remounted between idle and editing.
  const [ localValue, setLocalValue ] = useState<string>( String( value ?? '' ));

  useEffect(() => {
    if ( isEditing ) return;
    setLocalValue( String( value ?? '' ));
  }, [ value, isEditing ]);

  useEffect(() => {
    if ( !isEditing ) return;
    setLocalValue( initialValue !== undefined ? initialValue : String( value ?? '' ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const commitEdit = useCallback(() => {
      if ( !currentRow ) return;
      const newValues = { ...currentRow.values, [columnId]: localValue };

      dispatch( updateRow({ id: rowId, changes: { values: newValues }}));
      dispatch( cellValueChanged({ rowId, columnId, oldValue: currentRow.values[columnId],newValue: localValue }));
      dispatch( setEditingCell( null ));
    },
    [ dispatch, rowId, columnId, currentRow, localValue ]);

  const cancelEdit = useCallback(() => {
    dispatch( setEditingCell( null ));
  }, [dispatch]);

  const handleKeyDown = useCallback(( e: React.KeyboardEvent<HTMLInputElement> ) => {
      if ( !isEditing ) return;
      if ( e.key === 'Enter' ) {
        commitEdit();
      } else if ( e.key === 'Escape' ) {
        cancelEdit();
      }
    }, [ isEditing, commitEdit, cancelEdit ]);


  if ( !isEditing ) {
    return <span className={classes.cellValue}>{value}</span>;
  }

  return (
    <input
      className={classes.cellStringInput}
      type="number"
      value={localValue}
      onChange={( e ) => setLocalValue( e.currentTarget.value )}
      onKeyDown={handleKeyDown}
      onBlur={commitEdit}
      autoFocus
    />
  );
}
